import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  MenuItem,
  PrimaryButton,
  Tabs,
  Tab,
  TextField,
} from '@bb-ui/react-library/dist/components';
import { makeStyles, Theme } from '@bb-ui/react-library';
import { Refresh } from '@bb-ui/icons/dist/medium';
import { JobExecution } from '@lct-jobs/core';
import { PageTemplate } from 'components/PageTemplate';
import { ExecutionTable } from 'components/JobExecutionList/ExecutionTable';
import { MassOperationType, getMassOperationType } from 'components/JobExecution/massOperationType';
import { useRegionalExecutions } from 'hooks/useRegionalExecutions';
import { useExecutionSearch } from 'hooks/useExecutionSearch';

const useStyles = makeStyles((theme: Theme) => ({
  filtersContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  regionSelect: {
    minWidth: 180,
    backgroundColor: theme.palette.common.white,
  },
}));

type TabKey = 'all' | MassOperationType;

const TAB_KEYS: TabKey[] = ['all', MassOperationType.MASS_JOB, MassOperationType.MASS_QUERY];

const matchesTab = (execution: JobExecution, tab: TabKey) =>
  tab === 'all' || getMassOperationType(execution) === tab;

const useTabFromQuery = (): [TabKey, (tab: TabKey) => void] => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const requested = new URLSearchParams(search).get('type') as TabKey | null;
  const tab = requested && TAB_KEYS.includes(requested) ? requested : 'all';

  const setTab = React.useCallback(
    (next: TabKey) => {
      const params = new URLSearchParams(search);
      if (next === 'all') {
        params.delete('type');
      } else {
        params.set('type', next);
      }
      const query = params.toString();
      history.replace(query ? `${pathname}?${query}` : pathname);
    },
    [history, pathname, search],
  );

  return [tab, setTab];
};

const emptyStateKey: Record<TabKey, string> = {
  all: 'jobService.noData',
  [MassOperationType.MASS_JOB]: 'massQueryTooling.executions.noMassJobs',
  [MassOperationType.MASS_QUERY]: 'massQueryTooling.executions.noMassQueries',
};

export const RegionalExecutions: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    region,
    regions,
    setRegion,
    executions,
    loading,
    error,
    nextPageToken,
    loadMore,
    refresh,
  } = useRegionalExecutions();
  const [tab, setTab] = useTabFromQuery();
  const search = useExecutionSearch({ executions, nextPageToken, loading, loadMore });

  const counts = React.useMemo(
    () => ({
      all: search.matches.length,
      [MassOperationType.MASS_JOB]: search.matches.filter((e) =>
        matchesTab(e, MassOperationType.MASS_JOB),
      ).length,
      [MassOperationType.MASS_QUERY]: search.matches.filter((e) =>
        matchesTab(e, MassOperationType.MASS_QUERY),
      ).length,
    }),
    [search.matches],
  );

  const visible = React.useMemo(
    () => search.matches.filter((execution) => matchesTab(execution, tab)),
    [search.matches, tab],
  );

  // Counts only describe what has been fetched so far, so they stay labelled as partial until
  // the stream is exhausted. Until then an empty tab means "none loaded yet", not "none exist".
  const label = (key: TabKey, name: string) =>
    nextPageToken
      ? `${name} (${t('massQueryTooling.executions.loadedCount', { count: counts[key] })})`
      : `${name} (${counts[key]})`;

  // A search that has not finished walking must not be reported as "no match": the row the user
  // wants may still be on a page in flight.
  let searchEmptyMessage = t(emptyStateKey[tab]);
  if (search.searchPending) {
    searchEmptyMessage = t('jobService.executionSearching');
  } else if (search.isSearching) {
    searchEmptyMessage = t('jobService.executionNoMatch', { searchExpression: search.searchTerm });
  }

  return (
    <PageTemplate
      title={t('massQueryTooling.executions.pageTitle')}
      data-testid="regional-executions-list-page"
      headerControl={
        <PrimaryButton
          startIcon={<Refresh />}
          onClick={refresh}
          data-testid="regional-executions-refresh-button"
        >
          {t('global.refresh')}
        </PrimaryButton>
      }
    >
      <div className={classes.filtersContainer}>
        <TextField
          select
          id="region-select"
          label={t('massQueryTooling.selectRegionLabel')}
          value={region}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRegion(e.target.value)}
          className={classes.regionSelect}
          floatingLabel
        >
          {regions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </div>
      <Tabs
        value={TAB_KEYS.indexOf(tab)}
        onChange={(_: React.SyntheticEvent, index: number) => setTab(TAB_KEYS[index])}
        aria-label={t('massQueryTooling.executions.pageTitle')}
      >
        <Tab label={label('all', t('massQueryTooling.executions.tabs.all'))} />
        <Tab
          label={label(MassOperationType.MASS_JOB, t('massQueryTooling.executions.tabs.massJob'))}
        />
        <Tab
          label={label(
            MassOperationType.MASS_QUERY,
            t('massQueryTooling.executions.tabs.massQuery'),
          )}
        />
      </Tabs>
      {/* The table pages off the stream token, never the filtered rows, so a tab with no matches
          yet can still page forward instead of dead-ending on an empty state. */}
      <ExecutionTable
        executions={visible}
        loading={loading}
        error={error}
        nextPageToken={nextPageToken}
        onLoadMore={search.loadMorePage}
        emptyMessage={searchEmptyMessage}
        testIdPrefix="regional-executions"
        searchValue={search.inputValue}
        onSearchChange={search.onInputChange}
        searchFieldId="regional-executions-search"
        searchNotice={search.searchCapped ? t('jobService.executionSearchPartial') : undefined}
        showOperationType
        showUser
        detailPathBuilder={(execution) =>
          `/query-tooling/regions/${region}/executions/${execution.executionId}`
        }
      />
    </PageTemplate>
  );
};

export default RegionalExecutions;
